// FILE: src/components/security/VerifyMFAModal.js
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import CryptoJS from "crypto-js";
import { auth } from "../../firebase";
import {
  cacheLocalTrustedToken,
  decryptSecret,
  describeDevice,
  hasSessionBypass,
  isTrustedDevice,
  loadMfaProfile,
  saveMfaProfile,
  sendEmailMfaCode,
  setSessionBypass,
  updateTrustedDevice,
  verifyEmailMfaCode,
  verifyTotp,
} from "../../utils/mfa";

export default function VerifyMFAModal({ authUid, user }) {
  const [needed, setNeeded] = useState(false);
  const [loading, setLoading] = useState(true);

  const [method, setMethod] = useState("totp"); // totp | email (driven by profile)
  const [emailSent, setEmailSent] = useState(false);
  const [emailCooldown, setEmailCooldown] = useState(0);

  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [err, setErr] = useState("");

  // Compatible with either prop style — but always prefer actual Firebase auth uid when available.
  const uid = useMemo(() => authUid || auth.currentUser?.uid || user?.uid || "", [authUid, user?.uid]);

  // Cooldown tick
  useEffect(() => {
    if (!emailCooldown) return;
    const t = setInterval(() => setEmailCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [emailCooldown]);

  async function maybeSendEmailCode() {
    try {
      if (emailCooldown > 0) return;
      setErr("");
      await sendEmailMfaCode();
      setEmailSent(true);
      setEmailCooldown(30);
    } catch (e) {
      setErr(e?.message || "Failed to send email code");
    }
  }

  useEffect(() => {
    let isMounted = true;

    const openHandler = async () => {
      setNeeded(true);

      // If method is email, send a code immediately when the modal opens.
      try {
        const profile = await loadMfaProfile(uid);
        const m = (profile?.method || "totp").toLowerCase();
        setMethod(m === "email" ? "email" : "totp");
        if (m === "email") {
          await maybeSendEmailCode();
        }
      } catch {
        // ignore — modal will still show and allow recovery codes if available
      }
    };

    window.addEventListener("mfa:open", openHandler);

    (async () => {
      setLoading(true);
      setErr("");
      try {
        if (!uid) {
          setNeeded(false);
          return;
        }

        const profile = await loadMfaProfile(uid);
        if (!profile?.enabled) {
          setNeeded(false);
          return;
        }

        const m = (profile?.method || "totp").toLowerCase();
        setMethod(m === "email" ? "email" : "totp");

        const trusted = isTrustedDevice(profile, uid) || hasSessionBypass(uid);
        setNeeded(!trusted);

        if (!trusted && m === "email") {
          await maybeSendEmailCode();
        }
      } catch (e) {
        // Fail open to avoid lockouts if Firestore fails
        setNeeded(false);
        setErr(e?.message || "Unable to check MFA");
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
      window.removeEventListener("mfa:open", openHandler);
    };
  }, [uid]);

  async function handleVerify(e) {
    e.preventDefault();
    setErr("");

    try {
      const u = auth.currentUser;
      if (!u) throw new Error("Not authenticated");

      const profile = await loadMfaProfile(uid);
      if (!profile?.enabled) {
        setNeeded(false);
        return;
      }

      const normalized = code.trim();
      let verified = false;

      // 1) Recovery codes always allowed
      if (normalized.includes("-")) {
        const hash = CryptoJS.SHA256(normalized).toString(CryptoJS.enc.Hex);
        if (profile.backupCodeHashes?.includes(hash)) {
          verified = true;
          const nextBackupHashes = profile.backupCodeHashes.filter((h) => h !== hash);
          await saveMfaProfile(uid, { ...profile, backupCodeHashes: nextBackupHashes });
        }
      }

      // 2) Method-specific verification
      if (!verified) {
        if (method === "email") {
          await verifyEmailMfaCode(normalized);
          verified = true;
        } else {
          const secret = decryptSecret(profile.secretEnc, uid) || profile.secretBase32;
          if (!secret) throw new Error("MFA secret missing. Re-enable MFA in Security settings.");
          verified = verifyTotp(normalized, secret);
        }
      }

      if (!verified) throw new Error("Invalid code");

      // Mark this session as verified (bound to auth_time).
      setSessionBypass(uid, 30);

      if (rememberDevice) {
        const token =
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`;

        cacheLocalTrustedToken(uid, token, 30);

        await updateTrustedDevice(uid, {
          tokenHash: CryptoJS.SHA256(token).toString(CryptoJS.enc.Hex),
          createdAt: Date.now(),
          expiresAt: Date.now() + 1000 * 60 * 60 * 24 * 30,
          device: describeDevice(),
        });
      }

      setNeeded(false);
      setCode("");
      setEmailSent(false);

      // Let other pages (like /security) know MFA has been verified
      window.dispatchEvent(new CustomEvent("mfa:verified", { detail: { uid } }));
    } catch (e2) {
      setErr(e2?.message || "Invalid code");
    }
  }

  if (loading || !needed) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.55)",
        zIndex: 9999,
        display: "grid",
        placeItems: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          background: "#fff",
          borderRadius: 16,
          padding: 18,
          boxShadow: "0 18px 48px rgba(0,0,0,.22)",
          border: "1px solid rgba(0,0,0,.06)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              background: "#2a0516",
              display: "grid",
              placeItems: "center",
              color: "#fff",
              fontWeight: 800,
              letterSpacing: 0.5,
            }}
          >
            LW
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 16 }}>Two-Factor Verification</div>
            <div style={{ color: "#6b7280", fontSize: 12 }}>
              {method === "email"
                ? "Enter the code sent to your email (or use a recovery code)."
                : "Enter the code from your authenticator app (or use a recovery code)."}
            </div>
          </div>
        </div>

        {method === "email" ? (
          <div
            style={{
              background: "#f3f4f6",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: "10px 12px",
              marginBottom: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <div style={{ color: "#374151", fontSize: 13 }}>
              {emailSent ? "Email code sent. Check your inbox." : "Send a code to your email to continue."}
            </div>
            <button
              type="button"
              onClick={maybeSendEmailCode}
              disabled={emailCooldown > 0}
              style={{
                height: 34,
                padding: "0 12px",
                borderRadius: 10,
                border: "1px solid #2a0516",
                background: emailCooldown > 0 ? "#e5e7eb" : "#2a0516",
                color: emailCooldown > 0 ? "#6b7280" : "#fff",
                fontWeight: 700,
                cursor: emailCooldown > 0 ? "default" : "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {emailCooldown > 0 ? `Resend (${emailCooldown}s)` : "Send code"}
            </button>
          </div>
        ) : null}

        {err ? (
          <div
            style={{
              background: "#fee2e2",
              color: "#7a0b0b",
              border: "1px solid #fecaca",
              borderRadius: 10,
              padding: "8px 10px",
              marginBottom: 12,
            }}
          >
            {err}
          </div>
        ) : null}

        <form onSubmit={handleVerify}>
          <input
            autoFocus
            placeholder={method === "email" ? "Email code or recovery code" : "6-digit code or recovery code"}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="text"
            style={{
              width: "100%",
              height: 44,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              padding: "0 12px",
              outline: "none",
              fontSize: 14,
            }}
          />

          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
            />
            Trust this device for 30 days
          </label>

          <button
            type="submit"
            style={{
              width: "100%",
              height: 44,
              marginTop: 12,
              borderRadius: 12,
              border: "1px solid #2a0516",
              background: "#2a0516",
              color: "#fff",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Verify & Continue
          </button>

          <button
            type="button"
            onClick={() => auth.signOut()}
            style={{
              width: "100%",
              height: 44,
              marginTop: 10,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#fff",
              color: "#111827",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Logout
          </button>
        </form>

        <div style={{ marginTop: 10, color: "#6b7280", fontSize: 12 }}>
          Recovery codes look like <code>ABCD-EFGH-IJKL</code> and each can be used once.
        </div>
      </div>
    </div>
  );
}
